import { SITE_NAME } from '@/lib/config';
import { escapeScriptJson } from './escape';

export const DEFAULT_TITLE = `Granite Manufacturer in India | ${SITE_NAME}`;

/**
 * The social-card image of a page that has none of its own: a crop of one of the catalogue's stone photographs (1200 × 630,
 * `scripts/gen-og-image.ts` → `public/og-default.jpg`). A placeholder until real photography exists (C-25).
 */
export const DEFAULT_OG_IMAGE = '/og-default.jpg';
export const DEFAULT_OG_IMAGE_SIZE = { width: 1200, height: 630 } as const;

/** Open Graph `og:locale` (language_TERRITORY): the site is English only. */
export const OG_LOCALE = 'en_IN';

/** BCP 47 tag used for `inLanguage` in structured data (the same tag `Intl` gets, see `intlLocale`). */
export const LANGUAGE_TAG = 'en-IN';

/** `Shop` → `Shop | Shree Balaji Granites`; no title → the default brand title. */
export function formatTitle(title?: string): string {
  const trimmed = title?.trim();
  if (!trimmed) return DEFAULT_TITLE;
  return trimmed.includes(SITE_NAME) ? trimmed : `${trimmed} | ${SITE_NAME}`;
}

/** Serialise JSON-LD for an inline `<script>` (see `escapeScriptJson` for what is escaped and why). */
export function serializeJsonLd(data: Record<string, unknown>): string {
  return escapeScriptJson(JSON.stringify(data));
}

/**
 * schema.org types that are creative works, the only ones that have `inLanguage`: a `Product`, an
 * `Organization` or a `BreadcrumbList` with an `inLanguage` property would be invalid structured data.
 */
const TYPES_WITH_LANGUAGE: ReadonlySet<string> = new Set([
  'WebSite',
  'WebPage',
  'CollectionPage',
  'FAQPage',
  'AboutPage',
  'ContactPage',
  'ItemPage',
  'Article',
]);

/** Add `inLanguage` to the nodes that can carry it (and do not already say which language they are in). */
export function withInLanguage(data: Record<string, unknown>): Record<string, unknown> {
  const type = data['@type'];
  if (typeof type !== 'string' || !TYPES_WITH_LANGUAGE.has(type) || 'inLanguage' in data) {
    return data;
  }
  return { ...data, inLanguage: LANGUAGE_TAG };
}
